import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ChevronLeft, Coins, Crown, Snowflake, Settings, Gift, Plus,
  TrendingUp, ShieldAlert, Edit3, Trash2, Save, X, Lock, Unlock,
  DollarSign, Clock, Percent, Upload, Image as ImageIcon, Sparkles,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import GiftIcon from "../components/GiftIcon";
import AIGiftProposalDialog from "../components/AIGiftProposalDialog";

// FastAPI 422 responses return `detail` as an array of objects; rendering that
// inside a toast crashes React. Always coerce to a readable string.
const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => (x?.loc ? `${x.loc[x.loc.length - 1]}: ` : "") + (x?.msg || JSON.stringify(x))).join("; ");
  return typeof d === "string" && d ? d : fallback;
};


const COLLECTIONS = ["food", "dessert", "business", "hidden_gem", "legendary", "seasonal", "limited"];
const RARITIES = ["common", "rare", "epic", "legendary", "mythic"];
const ANIM_OPTIONS = [
  "gift-bounce", "gift-spin", "gift-flip", "gift-wiggle", "gift-glow", "gift-rotate",
  "gift-shine", "gift-rotate-shine", "gift-rainbow", "gift-flame", "gift-frost", "gift-storm",
  "gift-gem-pulse", "gift-diamond-shine", "gift-crown-shine", "gift-infinity",
  "gift-rocket", "gift-drive", "gift-dragon", "gift-galaxy", "gift-cosmic", "gift-hidden-feast",
];

function Section({ title, icon: Icon, children, right }) {
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-black tracking-tighter inline-flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-pink-400" />} {title}
        </h2>
        {right}
      </div>
      {children}
    </section>
  );
}

// --- Configurable-fees panel -----------------------------------------
// Each row edits one group of fees (percent + flat + enable toggles). All
// values sent server-side; the frontend NEVER computes fees on its own.
// A live audit log appears below.
const FEE_GROUPS = [
  {
    key: "credit_purchase",
    label: "Credit Purchase Fee",
    hint: "Applied on top of every Credit pack purchase.",
    pct_field: "credit_purchase_pct_bps",
    flat_field: "credit_purchase_flat_cents",
    pct_enabled: "credit_purchase_pct_enabled",
    flat_enabled: "credit_purchase_flat_enabled",
  },
  {
    key: "gift_purchase",
    label: "Gift/Gem Purchase Fee",
    hint: "Off by default. Turn on to add a fee when a user sends a Gift or Gem.",
    pct_field: "gift_purchase_pct_bps",
    flat_field: "gift_purchase_flat_cents",
    pct_enabled: "gift_purchase_pct_enabled",
    flat_enabled: "gift_purchase_flat_enabled",
  },
  {
    key: "gift_cashout",
    label: "Gift/Gem Cash-Out Fee",
    hint: "Applied to gift-derived earnings when a creator withdraws.",
    pct_field: "gift_cashout_pct_bps",
    flat_field: "gift_cashout_flat_cents",
    pct_enabled: "gift_cashout_pct_enabled",
    flat_enabled: "gift_cashout_flat_enabled",
  },
  {
    key: "order_cashout",
    label: "Order Earnings Cash-Out Fee",
    hint: "Applied when a vendor/creator withdraws marketplace order earnings. Default 2%. Snapshot on every cash-out.",
    pct_field: "order_cashout_pct_bps",
    flat_field: "order_cashout_flat_cents",
    pct_enabled: "order_cashout_pct_enabled",
    flat_enabled: "order_cashout_flat_enabled",
  },
  {
    key: "vendor_txn",
    label: "Vendor/Platform Transaction Fee",
    hint: "The Hidden Gem Service Fee at marketplace checkout.",
    pct_field: "vendor_txn_pct_bps",
    flat_field: "vendor_txn_flat_cents",
    pct_enabled: "vendor_txn_pct_enabled",
    flat_enabled: "vendor_txn_flat_enabled",
  },
];

function FeesPanel() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState([]);

  const load = useCallback(async () => {
    try {
      const [c, a] = await Promise.all([
        api.get("/admin/fees"),
        api.get("/admin/fees/audit"),
      ]);
      setCfg(c.data.config);
      setAudit(a.data.items);
    } catch (e) {
      toast.error("Couldn't load fees");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (delta) => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/fees", delta);
      setCfg(data.config);
      const a = await api.get("/admin/fees/audit");
      setAudit(a.data.items);
      toast.success("Saved. Future transactions will use the new fee.");
    } catch (e) {
      toast.error(errMsg(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <Section title="Fees" icon={DollarSign}><p className="text-zinc-500 text-sm">Loading…</p></Section>;

  return (
    <div className="space-y-5">
      <Section title="Financial Settings → Fees" icon={DollarSign} right={
        <span className="text-[10px] uppercase tracking-widest font-black text-cyan-300">
          server-side authority
        </span>
      }>
        <p className="text-xs text-zinc-400 mb-4">
          Changes take effect on <b className="text-white">new transactions only</b>. Historical rows keep the fee that was applied when they occurred.
        </p>
        <div className="grid gap-4">
          {FEE_GROUPS.map((g) => {
            const bps = cfg[g.pct_field] ?? 0;
            const flat = cfg[g.flat_field] ?? 0;
            const pctOn = cfg[g.pct_enabled] ?? true;
            const flatOn = cfg[g.flat_enabled] ?? true;
            return (
              <div key={g.key} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid={`fee-group-${g.key}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-black tracking-tight">{g.label}</h3>
                    <p className="text-xs text-zinc-400 mt-0.5">{g.hint}</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3 mt-3">
                  {/* Percent */}
                  <div className="rounded-xl bg-white/[0.03] border border-white/10 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] uppercase tracking-widest font-black text-pink-300 inline-flex items-center gap-1">
                        <Percent className="w-3 h-3" /> Percent
                      </label>
                      <button
                        onClick={() => patch({ [g.pct_enabled]: !pctOn })}
                        disabled={saving}
                        className={`h-6 px-3 rounded-full text-[10px] font-black ${pctOn ? "bg-cyan-400 text-black" : "bg-white/10 text-zinc-400"}`}
                        data-testid={`fee-toggle-${g.key}-pct`}
                      >
                        {pctOn ? "ON" : "OFF"}
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="50"
                        value={(bps / 100).toFixed(2)}
                        onChange={(e) => {
                          const pct = parseFloat(e.target.value || "0");
                          const newBps = Math.max(0, Math.min(5000, Math.round(pct * 100)));
                          setCfg({ ...cfg, [g.pct_field]: newBps });
                        }}
                        onBlur={() => patch({ [g.pct_field]: cfg[g.pct_field] })}
                        disabled={saving || !pctOn}
                        className="flex-1 h-9 rounded-lg bg-zinc-950 border border-white/10 text-white text-sm px-3 tabular-nums"
                        data-testid={`fee-input-${g.key}-pct`}
                      />
                      <span className="text-xs text-zinc-500">%</span>
                    </div>
                  </div>
                  {/* Flat */}
                  <div className="rounded-xl bg-white/[0.03] border border-white/10 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] uppercase tracking-widest font-black text-pink-300 inline-flex items-center gap-1">
                        <DollarSign className="w-3 h-3" /> Flat
                      </label>
                      <button
                        onClick={() => patch({ [g.flat_enabled]: !flatOn })}
                        disabled={saving}
                        className={`h-6 px-3 rounded-full text-[10px] font-black ${flatOn ? "bg-cyan-400 text-black" : "bg-white/10 text-zinc-400"}`}
                        data-testid={`fee-toggle-${g.key}-flat`}
                      >
                        {flatOn ? "ON" : "OFF"}
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-zinc-500">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={(flat / 100).toFixed(2)}
                        onChange={(e) => {
                          const dollars = parseFloat(e.target.value || "0");
                          const newCents = Math.max(0, Math.min(10000, Math.round(dollars * 100)));
                          setCfg({ ...cfg, [g.flat_field]: newCents });
                        }}
                        onBlur={() => patch({ [g.flat_field]: cfg[g.flat_field] })}
                        disabled={saving || !flatOn}
                        className="flex-1 h-9 rounded-lg bg-zinc-950 border border-white/10 text-white text-sm px-3 tabular-nums"
                        data-testid={`fee-input-${g.key}-flat`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Min gift cashout */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <h3 className="text-sm font-black tracking-tight">Minimum Gift/Gem Cash-Out</h3>
            <p className="text-xs text-zinc-400 mt-0.5">Recipients cannot withdraw less than this amount of Gift-derived earnings.</p>
            <div className="flex items-center gap-2 mt-3 max-w-xs">
              <span className="text-xs text-zinc-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={((cfg.min_gift_cashout_cents ?? 1000) / 100).toFixed(2)}
                onChange={(e) => {
                  const dollars = parseFloat(e.target.value || "0");
                  const newCents = Math.max(0, Math.round(dollars * 100));
                  setCfg({ ...cfg, min_gift_cashout_cents: newCents });
                }}
                onBlur={() => patch({ min_gift_cashout_cents: cfg.min_gift_cashout_cents })}
                disabled={saving}
                className="h-9 rounded-lg bg-zinc-950 border border-white/10 text-white text-sm px-3 tabular-nums flex-1"
                data-testid="fee-input-min-cashout"
              />
            </div>
          </div>
        </div>
      </Section>

      <Section title="Fee Change Audit Log" icon={Clock}>
        {audit.length === 0 && <p className="text-zinc-500 text-sm">No changes yet.</p>}
        <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="fee-audit-list">
          {audit.map((r) => (
            <div key={r.audit_id} className="text-xs bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-white truncate">
                  <span className="text-zinc-400">{r.field}:</span>{" "}
                  <span className="text-red-300 line-through">{String(r.old_value)}</span>{" → "}
                  <span className="text-cyan-300 font-black">{String(r.new_value)}</span>
                </p>
                <p className="text-[10px] text-zinc-500 truncate">by {r.changed_by_email}</p>
              </div>
              <span className="text-[10px] text-zinc-500 whitespace-nowrap tabular-nums">{new Date(r.changed_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

function GiftEditor({ initial, onSave, onCancel }) {
  const [aiOpen, setAiOpen] = useState(false);
  const [g, setG] = useState({
    name: initial?.name || "",
    collection: initial?.collection || "food",
    cost_credits: initial?.cost_credits || 50,
    rarity: initial?.rarity || "common",
    emoji: initial?.emoji || "🎁",
    animation_class: initial?.animation_class || "gift-bounce",
    full_screen: !!initial?.full_screen,
    description: initial?.description || "",
    is_active: initial?.is_active ?? true,
    is_seasonal: !!initial?.is_seasonal,
    is_limited: !!initial?.is_limited,
    // Phase D — custom artwork engine
    image_url: initial?.image_url || "",
    image_mime: initial?.image_mime || "",
    animation_speed_ms: initial?.animation_speed_ms ?? 1200,
    animation_scale_pct: initial?.animation_scale_pct ?? 100,
    loop_count: initial?.loop_count ?? 0,
    sound_url: initial?.sound_url || "",
  });
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = React.useRef(null);
  const set = (k, v) => setG((p) => ({ ...p, [k]: v }));

  const upload = async (file) => {
    if (!file) return;
    if (!/image\/(png|webp|gif|apng)/i.test(file.type)) {
      toast.error("Only PNG, WebP, GIF, or APNG (got: " + (file.type || "unknown") + ")");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large — max 5 MB");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/gifts/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setG((p) => ({ ...p, image_url: data.image_url, image_mime: data.image_mime }));
      toast.success("Artwork uploaded");
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };
  const clearImage = () => setG((p) => ({ ...p, image_url: "", image_mime: "" }));

  return (
    <div className="rounded-2xl border border-pink-400/30 bg-pink-500/[0.06] p-4 space-y-4" data-testid="gift-editor">
      {/* Live preview + name row */}
      <div className="flex items-center gap-3">
        <div className="w-20 h-20 rounded-2xl bg-zinc-900 border border-white/10 grid place-items-center overflow-hidden" data-testid="gift-editor-preview">
          <GiftIcon gift={g} size={56} />
        </div>
        <div className="flex-1 grid grid-cols-2 gap-2">
          <Input value={g.name} onChange={(e) => set("name", e.target.value)} placeholder="Name" data-testid="gift-editor-name" />
          <Input
            value={g.emoji}
            onChange={(e) => set("emoji", e.target.value)}
            placeholder="Fallback emoji"
            title="Used only if no image is uploaded"
            data-testid="gift-editor-emoji"
          />
        </div>
      </div>

      {/* Artwork uploader */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`rounded-xl border-2 border-dashed p-4 flex items-center gap-3 transition-colors ${dragOver ? "border-pink-400 bg-pink-500/10" : "border-white/10 bg-black/30"}`}
        data-testid="gift-editor-dropzone"
      >
        {g.image_url ? (
          <>
            <div className="w-14 h-14 rounded-lg bg-zinc-900 border border-white/10 overflow-hidden grid place-items-center">
              <img
                src={g.image_url.startsWith("http") ? g.image_url : `${process.env.REACT_APP_BACKEND_URL}${g.image_url}`}
                alt="artwork"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold truncate">Custom artwork uploaded</div>
              <div className="text-[10px] text-zinc-500 truncate">{g.image_mime}</div>
            </div>
            <Button onClick={clearImage} variant="outline" className="h-8 px-3 rounded-full text-[11px]" data-testid="gift-editor-remove-image">
              <X className="w-3 h-3 mr-1" /> Remove
            </Button>
          </>
        ) : (
          <>
            <ImageIcon className="w-8 h-8 text-zinc-500" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold">Drop PNG / WebP / GIF / APNG here</div>
              <div className="text-[10px] text-zinc-500">Max 5 MB. Transparent PNGs recommended.</div>
            </div>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/webp,image/gif,image/apng"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid="gift-editor-file-input"
        />
        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="h-8 px-3 rounded-full bg-pink-500 hover:bg-pink-400 text-[11px] font-bold"
          data-testid="gift-editor-upload-btn"
        >
          <Upload className="w-3 h-3 mr-1" />
          {uploading ? "Uploading…" : g.image_url ? "Replace" : "Upload"}
        </Button>
        <Button
          onClick={() => setAiOpen(true)}
          disabled={!initial?.gift_id}
          title={!initial?.gift_id ? "Save the gift first, then generate AI artwork" : "Generate artwork with AI"}
          className="h-8 px-3 rounded-full bg-gradient-to-r from-cyan-400 to-pink-500 text-black text-[11px] font-black disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="gift-editor-ai-btn"
        >
          <Sparkles className="w-3 h-3 mr-1" /> AI
        </Button>
      </div>

      <AIGiftProposalDialog
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        giftId={initial?.gift_id}
        previousImageUrl={g.image_url}
        onApproved={(gift) => {
          set("image_url", gift.image_url);
          set("image_mime", gift.image_mime);
        }}
      />

      {/* Collection / rarity / cost / animation */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <select value={g.collection} onChange={(e) => set("collection", e.target.value)} className="h-10 rounded-md bg-zinc-900 border border-white/10 px-2 text-sm" data-testid="gift-editor-collection">
          {COLLECTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={g.rarity} onChange={(e) => set("rarity", e.target.value)} className="h-10 rounded-md bg-zinc-900 border border-white/10 px-2 text-sm" data-testid="gift-editor-rarity">
          {RARITIES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <Input type="number" min={1} value={g.cost_credits} onChange={(e) => set("cost_credits", Number(e.target.value || 0))} placeholder="Cost" data-testid="gift-editor-cost" />
        <select value={g.animation_class} onChange={(e) => set("animation_class", e.target.value)} className="h-10 rounded-md bg-zinc-900 border border-white/10 px-2 text-sm" data-testid="gift-editor-anim">
          {ANIM_OPTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      {/* Animation controls (Phase D) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 rounded-xl bg-black/30 border border-white/10">
        <div>
          <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
            Speed · {g.animation_speed_ms} ms
          </label>
          <input
            type="range" min={300} max={5000} step={100}
            value={g.animation_speed_ms}
            onChange={(e) => set("animation_speed_ms", Number(e.target.value))}
            className="w-full accent-pink-500"
            data-testid="gift-editor-speed"
          />
        </div>
        <div>
          <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
            Scale · {g.animation_scale_pct}%
          </label>
          <input
            type="range" min={50} max={300} step={10}
            value={g.animation_scale_pct}
            onChange={(e) => set("animation_scale_pct", Number(e.target.value))}
            className="w-full accent-pink-500"
            data-testid="gift-editor-scale"
          />
        </div>
        <div>
          <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
            Loop · {g.loop_count === 0 ? "Infinite" : `${g.loop_count}x`}
          </label>
          <input
            type="range" min={0} max={20} step={1}
            value={g.loop_count}
            onChange={(e) => set("loop_count", Number(e.target.value))}
            className="w-full accent-pink-500"
            data-testid="gift-editor-loop"
          />
        </div>
      </div>

      <Input value={g.description} onChange={(e) => set("description", e.target.value)} placeholder="Description" data-testid="gift-editor-desc" />
      <Input
        value={g.sound_url}
        onChange={(e) => set("sound_url", e.target.value)}
        placeholder="Optional sound URL (mp3/ogg)"
        data-testid="gift-editor-sound"
      />
      <div className="flex flex-wrap gap-2 text-xs">
        {[
          ["full_screen", "Full-screen"], ["is_active", "Active"], ["is_seasonal", "Seasonal"], ["is_limited", "Limited"],
        ].map(([k, label]) => (
          <label key={k} className="inline-flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={!!g[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`gift-editor-${k}`} />
            {label}
          </label>
        ))}
      </div>
      <div className="flex gap-2 pt-2">
        <Button onClick={() => onSave(g)} className="h-9 px-3 rounded-full bg-pink-500 hover:bg-pink-400 text-xs font-bold" data-testid="gift-editor-save">
          <Save className="w-3.5 h-3.5 mr-1.5" /> Save
        </Button>
        <Button onClick={onCancel} className="h-9 px-3 rounded-full bg-white/5 border border-white/10 text-xs font-bold" data-testid="gift-editor-cancel">
          <X className="w-3.5 h-3.5 mr-1.5" /> Cancel
        </Button>
      </div>
    </div>
  );
}

// ---------- Phase E — Gem Editor (mirrors GiftEditor, gem-specific fields) ---
const GEM_TRACKS = ["both", "customer", "vendor"];
const GEM_CRITERIA = [
  { id: "min_score",             label: "Minimum reputation score" },
  { id: "min_completed_orders",  label: "Minimum completed orders" },
  { id: "min_followers",         label: "Minimum followers" },
  { id: "min_completed_sales",   label: "Minimum completed sales" },
  { id: "min_repeat_customers",  label: "Minimum repeat customers" },
  { id: "min_positive_reviews",  label: "Minimum positive reviews" },
];
const GEM_ANIM_CLASSES = [
  "gem-anim-neon", "gem-anim-prism", "gem-anim-ember",
  "gem-anim-diamond", "gem-anim-opal", "gem-anim-legendary",
];

function GemEditor({ initial, onSave, onCancel }) {
  const [g, setG] = useState({
    label: initial?.label || "",
    track: initial?.track || "both",
    rarity: initial?.rarity || "common",
    color: initial?.color || "#39FF14",
    description: initial?.description || "",
    emoji: initial?.emoji || "",
    tier_alias: initial?.tier_alias || "",
    criteria_type: initial?.criteria_type || "min_score",
    criteria_value: initial?.criteria_value ?? 0,
    is_active: initial?.is_active ?? true,
    image_url: initial?.image_url || "",
    image_mime: initial?.image_mime || "",
    animation_class: initial?.animation_class || "gem-anim-neon",
    animation_speed_ms: initial?.animation_speed_ms ?? 1200,
    animation_scale_pct: initial?.animation_scale_pct ?? 100,
    loop_count: initial?.loop_count ?? 0,
    sound_url: initial?.sound_url || "",
  });
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = React.useRef(null);
  const set = (k, v) => setG((p) => ({ ...p, [k]: v }));

  const upload = async (file) => {
    if (!file) return;
    if (!/image\/(png|webp|gif|apng)/i.test(file.type)) {
      toast.error("Only PNG, WebP, GIF, or APNG");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large — max 5 MB");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/gems/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setG((p) => ({ ...p, image_url: data.image_url, image_mime: data.image_mime }));
      toast.success("Artwork uploaded");
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };
  const onDrop = (e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) upload(f); };
  const clearImage = () => setG((p) => ({ ...p, image_url: "", image_mime: "" }));

  return (
    <div className="rounded-2xl border border-yellow-300/30 bg-yellow-300/[0.06] p-4 space-y-4" data-testid="gem-editor">
      <div className="flex items-center gap-3">
        <div className="w-20 h-20 rounded-2xl bg-zinc-900 border border-white/10 grid place-items-center overflow-hidden" data-testid="gem-editor-preview">
          <GiftIcon gift={{ ...g, name: g.label }} size={56} />
        </div>
        <div className="flex-1 grid grid-cols-2 gap-2">
          <Input value={g.label} onChange={(e) => set("label", e.target.value)} placeholder="Gem name" data-testid="gem-editor-label" />
          <Input value={g.emoji} onChange={(e) => set("emoji", e.target.value)} placeholder="Fallback emoji" data-testid="gem-editor-emoji" />
        </div>
      </div>

      {/* Artwork uploader */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`rounded-xl border-2 border-dashed p-4 flex items-center gap-3 transition-colors ${dragOver ? "border-yellow-300 bg-yellow-300/10" : "border-white/10 bg-black/30"}`}
        data-testid="gem-editor-dropzone"
      >
        {g.image_url ? (
          <>
            <div className="w-14 h-14 rounded-lg bg-zinc-900 border border-white/10 overflow-hidden grid place-items-center">
              <img src={g.image_url.startsWith("http") ? g.image_url : `${process.env.REACT_APP_BACKEND_URL}${g.image_url}`} alt="art" className="w-full h-full object-contain" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold truncate">Custom artwork uploaded</div>
              <div className="text-[10px] text-zinc-500 truncate">{g.image_mime}</div>
            </div>
            <Button onClick={clearImage} variant="outline" className="h-8 px-3 rounded-full text-[11px]" data-testid="gem-editor-remove-image">
              <X className="w-3 h-3 mr-1" /> Remove
            </Button>
          </>
        ) : (
          <>
            <ImageIcon className="w-8 h-8 text-zinc-500" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold">Drop PNG / WebP / GIF / APNG here</div>
              <div className="text-[10px] text-zinc-500">Max 5 MB. Transparent PNGs recommended.</div>
            </div>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/webp,image/gif,image/apng"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid="gem-editor-file-input"
        />
        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="h-8 px-3 rounded-full bg-yellow-300 text-black hover:bg-yellow-200 text-[11px] font-bold"
          data-testid="gem-editor-upload-btn"
        >
          <Upload className="w-3 h-3 mr-1" />
          {uploading ? "Uploading…" : g.image_url ? "Replace" : "Upload"}
        </Button>
      </div>

      {/* Meta row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <select value={g.track} onChange={(e) => set("track", e.target.value)} className="h-10 rounded-md bg-zinc-900 border border-white/10 px-2 text-sm" data-testid="gem-editor-track">
          {GEM_TRACKS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={g.rarity} onChange={(e) => set("rarity", e.target.value)} className="h-10 rounded-md bg-zinc-900 border border-white/10 px-2 text-sm" data-testid="gem-editor-rarity">
          {RARITIES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={g.animation_class} onChange={(e) => set("animation_class", e.target.value)} className="h-10 rounded-md bg-zinc-900 border border-white/10 px-2 text-sm" data-testid="gem-editor-anim">
          {GEM_ANIM_CLASSES.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <div className="flex items-center gap-2">
          <label className="text-[10px] font-bold text-zinc-400">Color</label>
          <input type="color" value={g.color} onChange={(e) => set("color", e.target.value)} className="h-9 w-14 rounded-md bg-zinc-900 border border-white/10" data-testid="gem-editor-color" />
        </div>
      </div>

      {/* Unlock criteria */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-3 rounded-xl bg-black/30 border border-white/10">
        <div>
          <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Unlock criteria</label>
          <select value={g.criteria_type} onChange={(e) => set("criteria_type", e.target.value)} className="w-full h-10 rounded-md bg-zinc-900 border border-white/10 px-2 text-sm mt-1" data-testid="gem-editor-criteria-type">
            {GEM_CRITERIA.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Minimum value</label>
          <Input type="number" min={0} value={g.criteria_value} onChange={(e) => set("criteria_value", Number(e.target.value || 0))} data-testid="gem-editor-criteria-value" />
        </div>
      </div>

      {/* Animation controls */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 rounded-xl bg-black/30 border border-white/10">
        <div>
          <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Speed · {g.animation_speed_ms} ms</label>
          <input type="range" min={300} max={5000} step={100} value={g.animation_speed_ms} onChange={(e) => set("animation_speed_ms", Number(e.target.value))} className="w-full accent-yellow-300" data-testid="gem-editor-speed" />
        </div>
        <div>
          <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Scale · {g.animation_scale_pct}%</label>
          <input type="range" min={50} max={300} step={10} value={g.animation_scale_pct} onChange={(e) => set("animation_scale_pct", Number(e.target.value))} className="w-full accent-yellow-300" data-testid="gem-editor-scale" />
        </div>
        <div>
          <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Loop · {g.loop_count === 0 ? "Infinite" : `${g.loop_count}x`}</label>
          <input type="range" min={0} max={20} step={1} value={g.loop_count} onChange={(e) => set("loop_count", Number(e.target.value))} className="w-full accent-yellow-300" data-testid="gem-editor-loop" />
        </div>
      </div>

      <Input value={g.description} onChange={(e) => set("description", e.target.value)} placeholder="Description" data-testid="gem-editor-desc" />

      <div className="flex gap-2 pt-2">
        <Button onClick={() => onSave(g)} className="h-9 px-3 rounded-full bg-yellow-300 text-black hover:bg-yellow-200 text-xs font-bold" data-testid="gem-editor-save">
          <Save className="w-3.5 h-3.5 mr-1.5" /> Save
        </Button>
        <Button onClick={onCancel} className="h-9 px-3 rounded-full bg-white/5 border border-white/10 text-xs font-bold" data-testid="gem-editor-cancel">
          <X className="w-3.5 h-3.5 mr-1.5" /> Cancel
        </Button>
      </div>
    </div>
  );
}

export default function AdminEconomy() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("metrics");
  const [metrics, setMetrics] = useState(null);
  const [gifts, setGifts] = useState([]);
  const [gems, setGems] = useState([]);
  const [settings, setSettings] = useState(null);
  const [editingId, setEditingId] = useState(null); // gift_id or "new"
  const [editingGemId, setEditingGemId] = useState(null); // gem_id or "new-gem"
  const [freezeUserId, setFreezeUserId] = useState("");
  const [grantUserId, setGrantUserId] = useState("");
  const [grantAmount, setGrantAmount] = useState(100);

  const isMaster = user && ["ceo", "master_admin"].includes(user.role);

  const load = useCallback(async () => {
    try {
      const [m, g, gm, s] = await Promise.all([
        api.get("/admin/economy/metrics"),
        api.get("/admin/gifts/all"),
        api.get("/admin/gems/all"),
        api.get("/admin/economy/settings"),
      ]);
      setMetrics(m.data);
      setGifts(g.data.items);
      setGems(gm.data.items);
      setSettings(s.data);
    } catch (e) {
      toast.error("Couldn't load economy data");
    }
  }, []);
  useEffect(() => { if (isMaster) load(); }, [isMaster, load]);

  if (!isMaster) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="admin-economy-denied">
        <Lock className="w-10 h-10 mx-auto text-zinc-600 mb-3" />
        <h1 className="text-2xl font-black tracking-tighter">Master Admin only</h1>
        <p className="text-sm text-zinc-400 mt-2">This is the Gift Economy Control Center. Only the CEO / Master Admin has access.</p>
        <Button onClick={() => navigate(-1)} className="mt-4 rounded-full">Go back</Button>
      </div>
    );
  }

  const saveGift = async (form) => {
    try {
      if (editingId === "new") {
        await api.post("/admin/gifts", form);
        toast.success("Gift created");
      } else {
        await api.patch(`/admin/gifts/${editingId}`, form);
        toast.success("Gift updated");
      }
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Save failed"));
    }
  };
  const deactivateGift = async (gift_id) => {
    if (!window.confirm("Deactivate this gift? It'll disappear from the user picker but stay visible to you under 'Inactive' where you can reactivate it later.")) return;
    // Optimistic UI update
    setGifts((prev) => prev.map((g) => (g.gift_id === gift_id ? { ...g, is_active: false } : g)));
    try {
      await api.post(`/admin/gifts/${gift_id}/deactivate`);
      toast.success("Gift deactivated");
    } catch (e) {
      toast.error("Failed to deactivate");
      load();
    }
  };
  const activateGift = async (gift_id) => {
    setGifts((prev) => prev.map((g) => (g.gift_id === gift_id ? { ...g, is_active: true } : g)));
    try {
      await api.post(`/admin/gifts/${gift_id}/activate`);
      toast.success("Gift reactivated");
    } catch (e) {
      toast.error("Failed to reactivate");
      load();
    }
  };
  const deleteGift = async (gift_id, gift_name) => {
    if (!window.confirm(`Delete "${gift_name || 'this gift'}"?\n\nIt will be permanently removed from your Gift management screen. This cannot be undone.`)) return;
    // Optimistic — remove from local list immediately
    const backup = gifts;
    setGifts((prev) => prev.filter((g) => g.gift_id !== gift_id));
    try {
      const { data } = await api.delete(`/admin/gifts/${gift_id}`);
      if (data.action === "hard_deleted") {
        toast.success("Gift permanently deleted");
      } else {
        toast.success("Gift deleted (history preserved for accounting)");
      }
    } catch (e) {
      setGifts(backup);
      toast.error(errMsg(e, "Delete failed"));
    }
  };
  // ---- Phase E — Gem CRUD handlers (mirrors Gift) ----
  const saveGem = async (form) => {
    try {
      if (editingGemId === "new-gem") {
        await api.post("/admin/gems", form);
        toast.success("Gem created");
      } else {
        await api.patch(`/admin/gems/${editingGemId}`, form);
        toast.success("Gem updated");
      }
      setEditingGemId(null);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Save failed"));
    }
  };
  const deactivateGem = async (gem_id) => {
    if (!window.confirm("Deactivate this gem? It'll be hidden from users but stay visible to you under 'Inactive'.")) return;
    setGems((prev) => prev.map((g) => (g.gem_id === gem_id ? { ...g, is_active: false } : g)));
    try { await api.post(`/admin/gems/${gem_id}/deactivate`); toast.success("Gem deactivated"); }
    catch (e) { toast.error("Failed"); load(); }
  };
  const activateGem = async (gem_id) => {
    setGems((prev) => prev.map((g) => (g.gem_id === gem_id ? { ...g, is_active: true } : g)));
    try { await api.post(`/admin/gems/${gem_id}/activate`); toast.success("Gem reactivated"); }
    catch (e) { toast.error("Failed"); load(); }
  };
  const deleteGem = async (gem_id, label) => {
    if (!window.confirm(`Delete "${label || 'this gem'}"?\n\nIt will be permanently removed from your Gem management screen. This cannot be undone.`)) return;
    const backup = gems;
    setGems((prev) => prev.filter((g) => g.gem_id !== gem_id));
    try {
      const { data } = await api.delete(`/admin/gems/${gem_id}`);
      if (data.action === "hard_deleted") toast.success("Gem permanently deleted");
      else toast.success("Gem deleted (users who equipped it keep it)");
    } catch (e) {
      setGems(backup);
      toast.error(errMsg(e, "Delete failed"));
    }
  };
  const saveSettings = async (patch) => {
    try {
      const { data } = await api.put("/admin/economy/settings", patch);
      setSettings(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(errMsg(e, "Failed"));
    }
  };
  const grant = async () => {
    if (!grantUserId.trim()) return toast.error("user_id required");
    try {
      const { data } = await api.post("/admin/wallets/grant", {
        user_id: grantUserId.trim(),
        credits: Number(grantAmount),
        reason: "admin grant",
      });
      toast.success(`Granted ${grantAmount} credits. New balance: ${data.wallet.credits}`);
    } catch (e) {
      toast.error(errMsg(e, "Grant failed"));
    }
  };
  const freeze = async (frozen) => {
    if (!freezeUserId.trim()) return toast.error("user_id required");
    try {
      await api.post("/admin/wallets/freeze", { user_id: freezeUserId.trim(), frozen, reason: frozen ? "admin freeze" : "admin unfreeze" });
      toast.success(frozen ? "Wallet frozen" : "Wallet unfrozen");
    } catch (e) {
      toast.error(errMsg(e, "Failed"));
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 pb-24 space-y-6" data-testid="admin-economy-page">
      <button onClick={() => navigate("/admin")} className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white" data-testid="admin-economy-back">
        <ChevronLeft className="w-4 h-4" /> Back to Admin
      </button>

      <header className="relative overflow-hidden rounded-3xl border border-yellow-300/40 bg-gradient-to-br from-yellow-400/15 via-zinc-950 to-pink-500/15 p-6">
        <p className="inline-flex items-center gap-2 text-yellow-300 text-[10px] uppercase tracking-[0.3em] font-black">
          <Crown className="w-3 h-3" /> Master Admin
        </p>
        <h1 className="text-4xl font-black tracking-tighter mt-2">Gift Economy<br /><span className="bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">Control Center</span></h1>
        <p className="text-sm text-zinc-300 mt-2">Tune the entire credits + gifting + wallet economy. Changes are immediate and global.</p>
      </header>

      <nav className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="admin-economy-tabs">
        {[
          ["metrics", "Revenue", TrendingUp],
          ["gifts", "Gifts", Gift],
          ["gems", "Gems", Crown],
          ["settings", "Economy", Settings],
          ["fees", "Fees", DollarSign],
          ["wallets", "Wallets", ShieldAlert],
        ].map(([k, label, Icon]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`shrink-0 rounded-full h-9 px-4 text-xs font-bold uppercase tracking-widest border inline-flex items-center gap-1.5 ${
              tab === k ? "bg-yellow-300/20 text-yellow-200 border-yellow-300/40" : "bg-white/5 text-zinc-300 border-white/10"
            }`}
            data-testid={`admin-economy-tab-${k}`}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </nav>

      {/* METRICS */}
      {tab === "metrics" && metrics && (
        <Section title="Revenue Analytics (30 days)" icon={TrendingUp}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-2xl border border-yellow-300/30 bg-yellow-300/[0.05] p-4">
              <p className="text-[10px] uppercase tracking-widest text-yellow-300 font-bold">USD revenue</p>
              <p className="text-3xl font-black tracking-tighter mt-1" data-testid="metric-revenue">${metrics.revenue_30d_usd.toLocaleString()}</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Purchases</p>
              <p className="text-3xl font-black tracking-tighter mt-1">{metrics.purchases_30d.toLocaleString()}</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Credits sold</p>
              <p className="text-3xl font-black tracking-tighter mt-1">{metrics.credits_sold_30d.toLocaleString()}</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Gifts sent</p>
              <p className="text-3xl font-black tracking-tighter mt-1">{metrics.gifts_30d.toLocaleString()}</p>
            </div>
            <div className="rounded-2xl border border-cyan-400/30 bg-cyan-400/[0.05] p-4">
              <p className="text-[10px] uppercase tracking-widest text-cyan-300 font-bold">Gift credits volume</p>
              <p className="text-3xl font-black tracking-tighter mt-1">{metrics.gift_volume_credits_30d.toLocaleString()}</p>
            </div>
            <div className="rounded-2xl border border-pink-400/30 bg-pink-400/[0.05] p-4">
              <p className="text-[10px] uppercase tracking-widest text-pink-300 font-bold">Platform earnings</p>
              <p className="text-3xl font-black tracking-tighter mt-1">{metrics.platform_credits_30d.toLocaleString()}</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Frozen wallets</p>
              <p className="text-3xl font-black tracking-tighter mt-1">{metrics.frozen_wallets}</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Active meters</p>
              <p className="text-3xl font-black tracking-tighter mt-1">{metrics.active_support_meters}</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <h3 className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-2">Top earners (7d)</h3>
              {metrics.top_earners_7d.length === 0 ? <p className="text-xs text-zinc-500">No data yet</p> : (
                <div className="space-y-1.5">
                  {metrics.top_earners_7d.map((e) => (
                    <div key={e.user_id} className="flex items-center justify-between text-sm">
                      <span className="truncate">{e.name}</span>
                      <span className="text-cyan-300 font-bold inline-flex items-center gap-1"><Coins className="w-3 h-3" />{e.credits.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <h3 className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-2">Top supporters (7d)</h3>
              {metrics.top_supporters_7d.length === 0 ? <p className="text-xs text-zinc-500">No data yet</p> : (
                <div className="space-y-1.5">
                  {metrics.top_supporters_7d.map((e) => (
                    <div key={e.user_id} className="flex items-center justify-between text-sm">
                      <span className="truncate">{e.name}</span>
                      <span className="text-pink-300 font-bold inline-flex items-center gap-1"><Coins className="w-3 h-3" />{e.credits.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {metrics.top_gifts.length > 0 && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 mt-4">
              <h3 className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-2">Best performing gifts (lifetime)</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {metrics.top_gifts.map((g) => (
                  <div key={g.gift_id} className="rounded-xl bg-white/5 p-2 flex items-center gap-2">
                    <GiftIcon gift={g} size={24} paused />
                    <div className="min-w-0">
                      <p className="text-xs font-bold truncate">{g.name}</p>
                      <p className="text-[10px] text-yellow-300 font-black inline-flex items-center gap-0.5"><Coins className="w-2.5 h-2.5" />{g.credits.toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>
      )}

      {/* GIFTS */}
      {tab === "gifts" && (
        <Section
          title="Gift catalog"
          icon={Gift}
          right={(
            <Button onClick={() => setEditingId("new")} className="rounded-full bg-pink-500 hover:bg-pink-400 h-9 px-4 text-xs font-bold" data-testid="admin-create-gift-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> New gift
            </Button>
          )}
        >
          {editingId === "new" && (
            <div className="mb-3">
              <GiftEditor onSave={saveGift} onCancel={() => setEditingId(null)} />
            </div>
          )}
          {(() => {
            const active = gifts.filter((g) => g.is_active);
            const inactive = gifts.filter((g) => !g.is_active);
            const renderRow = (g) => (
              <div key={g.gift_id} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3" data-testid={`admin-gift-row-${g.gift_id}`}>
                {editingId === g.gift_id ? (
                  <GiftEditor initial={g} onSave={saveGift} onCancel={() => setEditingId(null)} />
                ) : (
                  <div className="flex items-center gap-3">
                    <div className={`w-14 h-14 rounded-2xl bg-zinc-900 border border-white/10 grid place-items-center rarity-${g.rarity}`}>
                      <GiftIcon gift={g} size={32} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm truncate">{g.name} <span className="text-[10px] text-zinc-500">· {g.collection} · {g.rarity}</span></p>
                      <p className="text-xs text-yellow-300 font-black inline-flex items-center gap-1"><Coins className="w-3 h-3" />{g.cost_credits.toLocaleString()}</p>
                      {!g.is_active && <span className="ml-2 text-[10px] uppercase tracking-widest text-red-300 font-bold">Inactive</span>}
                      {g.full_screen && <span className="ml-2 text-[10px] uppercase tracking-widest text-yellow-300 font-bold">Hero</span>}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        onClick={() => setEditingId(g.gift_id)}
                        className="h-8 w-8 p-0 rounded-full bg-white/5 border border-white/10"
                        title="Edit"
                        data-testid={`admin-edit-gift-${g.gift_id}`}
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </Button>
                      {g.is_active ? (
                        <Button
                          onClick={() => deactivateGift(g.gift_id)}
                          className="h-8 px-2 rounded-full bg-white/5 border border-white/10 text-yellow-300 text-[10px] font-bold"
                          title="Deactivate (stays in DB, hidden from users)"
                          data-testid={`admin-deactivate-gift-${g.gift_id}`}
                        >
                          <Lock className="w-3 h-3 mr-1" /> Deactivate
                        </Button>
                      ) : (
                        <Button
                          onClick={() => activateGift(g.gift_id)}
                          className="h-8 px-2 rounded-full bg-white/5 border border-white/10 text-emerald-300 text-[10px] font-bold"
                          title="Reactivate"
                          data-testid={`admin-activate-gift-${g.gift_id}`}
                        >
                          <Unlock className="w-3 h-3 mr-1" /> Activate
                        </Button>
                      )}
                      <Button
                        onClick={() => deleteGift(g.gift_id, g.name)}
                        className="h-8 w-8 p-0 rounded-full bg-red-500/10 border border-red-400/30 text-red-300 hover:bg-red-500/20"
                        title="Delete permanently"
                        data-testid={`admin-delete-gift-${g.gift_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
            return (
              <>
                <div className="flex items-center gap-2 mt-1 mb-2">
                  <h4 className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">Active</h4>
                  <span className="text-[10px] text-zinc-500">({active.length})</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admin-gifts-active-list">
                  {active.length === 0 ? (
                    <p className="text-xs text-zinc-500 col-span-full">No active gifts yet — click <span className="text-pink-300">New gift</span> to add one.</p>
                  ) : active.map(renderRow)}
                </div>
                {inactive.length > 0 && (
                  <>
                    <div className="flex items-center gap-2 mt-6 mb-2">
                      <h4 className="text-[10px] uppercase tracking-widest text-zinc-400 font-black">Inactive</h4>
                      <span className="text-[10px] text-zinc-500">({inactive.length}) · hidden from users, still here for you</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 opacity-70" data-testid="admin-gifts-inactive-list">
                      {inactive.map(renderRow)}
                    </div>
                  </>
                )}
              </>
            );
          })()}
        </Section>
      )}

      {/* GEMS */}
      {tab === "gems" && (
        <Section
          title="Gem Library"
          icon={Crown}
          right={(
            <Button onClick={() => setEditingGemId("new-gem")} className="rounded-full bg-yellow-300 text-black hover:bg-yellow-200 h-9 px-4 text-xs font-bold" data-testid="admin-create-gem-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> New gem
            </Button>
          )}
        >
          {editingGemId === "new-gem" && (
            <div className="mb-3">
              <GemEditor onSave={saveGem} onCancel={() => setEditingGemId(null)} />
            </div>
          )}
          {(() => {
            const active = gems.filter((g) => g.is_active);
            const inactive = gems.filter((g) => !g.is_active);
            const renderRow = (g) => (
              <div key={g.gem_id} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3" data-testid={`admin-gem-row-${g.gem_id}`}>
                {editingGemId === g.gem_id ? (
                  <GemEditor initial={g} onSave={saveGem} onCancel={() => setEditingGemId(null)} />
                ) : (
                  <div className="flex items-center gap-3">
                    <div className={`w-14 h-14 rounded-2xl bg-zinc-900 border border-white/10 grid place-items-center rarity-${g.rarity}`}>
                      <GiftIcon gift={{ ...g, name: g.label }} size={32} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm truncate">{g.label} <span className="text-[10px] text-zinc-500">· {g.track} · {g.rarity}</span></p>
                      <p className="text-[10px] text-zinc-500">Unlocks at {g.criteria_type?.replace('min_', '').replace(/_/g, ' ')}: {g.criteria_value}</p>
                      {!g.is_active && <span className="text-[10px] uppercase tracking-widest text-red-300 font-bold">Inactive</span>}
                    </div>
                    <div className="flex gap-1">
                      <Button onClick={() => setEditingGemId(g.gem_id)} className="h-8 w-8 p-0 rounded-full bg-white/5 border border-white/10" title="Edit" data-testid={`admin-edit-gem-${g.gem_id}`}>
                        <Edit3 className="w-3.5 h-3.5" />
                      </Button>
                      {g.is_active ? (
                        <Button onClick={() => deactivateGem(g.gem_id)} className="h-8 px-2 rounded-full bg-white/5 border border-white/10 text-yellow-300 text-[10px] font-bold" title="Deactivate" data-testid={`admin-deactivate-gem-${g.gem_id}`}>
                          <Lock className="w-3 h-3 mr-1" /> Deactivate
                        </Button>
                      ) : (
                        <Button onClick={() => activateGem(g.gem_id)} className="h-8 px-2 rounded-full bg-white/5 border border-white/10 text-emerald-300 text-[10px] font-bold" title="Reactivate" data-testid={`admin-activate-gem-${g.gem_id}`}>
                          <Unlock className="w-3 h-3 mr-1" /> Activate
                        </Button>
                      )}
                      <Button onClick={() => deleteGem(g.gem_id, g.label)} className="h-8 w-8 p-0 rounded-full bg-red-500/10 border border-red-400/30 text-red-300 hover:bg-red-500/20" title="Delete permanently" data-testid={`admin-delete-gem-${g.gem_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
            return (
              <>
                <div className="flex items-center gap-2 mt-1 mb-2">
                  <h4 className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">Active</h4>
                  <span className="text-[10px] text-zinc-500">({active.length})</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admin-gems-active-list">
                  {active.length === 0 ? (
                    <p className="text-xs text-zinc-500 col-span-full">No custom gems yet — click <span className="text-yellow-300">New gem</span> to add one.</p>
                  ) : active.map(renderRow)}
                </div>
                {inactive.length > 0 && (
                  <>
                    <div className="flex items-center gap-2 mt-6 mb-2">
                      <h4 className="text-[10px] uppercase tracking-widest text-zinc-400 font-black">Inactive</h4>
                      <span className="text-[10px] text-zinc-500">({inactive.length}) · hidden from users, still here for you</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 opacity-70" data-testid="admin-gems-inactive-list">
                      {inactive.map(renderRow)}
                    </div>
                  </>
                )}
              </>
            );
          })()}
        </Section>
      )}

      {/* SETTINGS */}
      {tab === "settings" && settings && (
        <Section title="Economy Settings" icon={Settings}>
          <div className="grid md:grid-cols-3 gap-3">
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Creator share %</p>
              <Input
                type="number"
                min={10}
                max={100}
                defaultValue={settings.recipient_share_pct}
                onBlur={(e) => saveSettings({ recipient_share_pct: Number(e.target.value) })}
                className="mt-2"
                data-testid="settings-recipient-share"
              />
              <p className="text-[10px] text-zinc-500 mt-1">Platform takes the rest. Default 70%.</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Min cashout credits</p>
              <Input
                type="number"
                min={100}
                defaultValue={settings.min_cashout_credits}
                onBlur={(e) => saveSettings({ min_cashout_credits: Number(e.target.value) })}
                className="mt-2"
                data-testid="settings-min-cashout"
              />
              <p className="text-[10px] text-zinc-500 mt-1">2500 credits ≈ $25 (Phase 3 cashout flow).</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Support meter threshold</p>
              <Input
                type="number"
                min={10}
                defaultValue={settings.support_meter_threshold}
                onBlur={(e) => saveSettings({ support_meter_threshold: Number(e.target.value) })}
                className="mt-2"
                data-testid="settings-support-threshold"
              />
              <p className="text-[10px] text-zinc-500 mt-1">7-day credits earned to unlock 24h feature boost.</p>
            </div>
          </div>
        </Section>
      )}

      {/* WALLETS */}
      {tab === "fees" && <FeesPanel />}

      {tab === "wallets" && (
        <Section title="Wallet Management" icon={ShieldAlert}>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-cyan-400/30 bg-cyan-400/[0.05] p-4 space-y-2">
              <h3 className="text-sm font-black tracking-tight inline-flex items-center gap-2"><Coins className="w-4 h-4 text-cyan-300" /> Grant / revoke credits</h3>
              <Input placeholder="user_id" value={grantUserId} onChange={(e) => setGrantUserId(e.target.value)} data-testid="grant-user-input" />
              <Input type="number" placeholder="credits (negative to revoke)" value={grantAmount} onChange={(e) => setGrantAmount(Number(e.target.value))} data-testid="grant-amount-input" />
              <Button onClick={grant} className="w-full h-10 rounded-full bg-cyan-500 hover:bg-cyan-400 text-zinc-950 font-bold text-xs" data-testid="grant-submit-btn">
                Apply
              </Button>
            </div>
            <div className="rounded-2xl border border-red-400/30 bg-red-400/[0.05] p-4 space-y-2">
              <h3 className="text-sm font-black tracking-tight inline-flex items-center gap-2"><Snowflake className="w-4 h-4 text-red-300" /> Freeze / unfreeze wallet</h3>
              <Input placeholder="user_id" value={freezeUserId} onChange={(e) => setFreezeUserId(e.target.value)} data-testid="freeze-user-input" />
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={() => freeze(true)} className="h-10 rounded-full bg-red-500 hover:bg-red-400 text-white font-bold text-xs" data-testid="freeze-submit-btn">
                  <Lock className="w-3.5 h-3.5 mr-1" /> Freeze
                </Button>
                <Button onClick={() => freeze(false)} className="h-10 rounded-full bg-white/5 border border-white/10 text-white font-bold text-xs" data-testid="unfreeze-submit-btn">
                  <Unlock className="w-3.5 h-3.5 mr-1" /> Unfreeze
                </Button>
              </div>
            </div>
          </div>
        </Section>
      )}
    </div>
  );
}
